import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";

const DataGridSkeletonForEmailSubscribers = ({
    rows = 6,
    showPagination = true,
}) => {
    return (
        <Box sx={{ width: '100%' }}>

            {/* Search Bar */}
            <Box sx={{ mb: 2 }}>
                <Skeleton
                    variant="rectangular"
                    width="30%"
                    height={30}
                    sx={{ borderRadius: "12px", maxWidth: '600px' }}
                />
            </Box>

            {/* Filters Card — fully static, no shimmer */}
            <Box sx={{
                mb: 2,
                p: 2,
                background: '#eee',
                borderRadius: '10px',
                animation: 'none !important',
                '& *': {
                    animation: 'none !important',
                    backgroundImage: 'none !important',
                },
            }}>
                <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}
                >
                    Admin Filters (Optional)
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ mb: 2, color: 'text.secondary' }}
                >
                   By default, view all email subscribers across all bakeries and locations.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Dropdown 1 — static */}
                    <Box sx={{
                        flex: 1,
                        height: 40,
                        borderRadius: '4px',
                        border: '1px solid rgba(0,0,0,0.23)',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                    }}>
                        <Typography variant="body2" sx={{ color: 'text.disabled', flex: 1 }}>
                            Select a bakery
                        </Typography>
                        <Box component="span" sx={{ color: 'text.disabled', fontSize: 18 }}>▾</Box>
                    </Box>

                    {/* Dropdown 2 — static */}
                    <Box sx={{
                        flex: 1,
                        height: 40,
                        borderRadius: '4px',
                        border: '1px solid rgba(0,0,0,0.23)',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                    }}>
                        <Typography variant="body2" sx={{ color: 'text.disabled', flex: 1 }}>
                            Select Bakery Location
                        </Typography>
                        <Box component="span" sx={{ color: 'text.disabled', fontSize: 18 }}>▾</Box>
                    </Box>
                </Box>
            </Box>

            {/* Table Container — matches Documentation style */}
            <Box sx={{ overflow: 'auto', width: '100%' }}>
                <Box sx={{
                    width: '100%',
                    display: 'table',
                    tableLayout: 'fixed',
                    minWidth: '1400px',
                    borderRadius: "12px",
                    backgroundColor: (theme) => theme.palette.background.paper,
                    border: "1px solid rgba(0,0,0,0.12)",
                    overflow: "hidden",
                }}>

                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        py: 2.5,
                        backgroundColor: "rgba(0,0,0,0.03)",
                        borderBottom: "2px solid rgba(0,0,0,0.12)",
                    }}>
                        <Box sx={{ flex: 1, minWidth: 150 }}>
                            <Skeleton variant="text" height={26} width="60%" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ flex: 1.2, minWidth: 200 }}>
                            <Skeleton variant="text" height={26} width="50%" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ width: 120 }}>
                            <Skeleton variant="text" height={26} width="70%" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 180 }}>
                            <Skeleton variant="text" height={26} width="60%" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Skeleton variant="text" height={26} width="70%" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ width: 180 }}>
                            <Skeleton variant="text" height={26} width="85%" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ width: 200 }}>
                            <Skeleton variant="text" height={26} width="90%" sx={{ fontWeight: 600 }} />
                        </Box>
                    </Box>

                    {/* Rows */}
                    {Array.from({ length: rows }).map((_, rowIdx) => (
                        <Box
                            key={rowIdx}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 3,
                                py: 2.2,
                                borderBottom: rowIdx !== rows - 1
                                    ? "1px solid rgba(0,0,0,0.06)"
                                    : "none",
                                '&:hover': {
                                    backgroundColor: "rgba(0,121,255,0.04)",
                                },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 150 }}>
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    width={`${65 - rowIdx * 1.5}%`}
                                    sx={{ borderRadius: "4px" }}
                                />
                            </Box>

                            <Box sx={{ flex: 1.2, minWidth: 200 }}>
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    width={`${75 - rowIdx * 1}%`}
                                    sx={{ borderRadius: "4px" }}
                                />
                            </Box>

                            <Box sx={{ width: 120 }}>
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    width="60%"
                                    sx={{ borderRadius: "4px" }}
                                />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    width="70%"
                                    sx={{ borderRadius: "4px" }}
                                />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Skeleton
                                    variant="rectangular"
                                    height={20}
                                    width="65%"
                                    sx={{ borderRadius: "4px" }}
                                />
                            </Box>

                            {/* Doc Added Chip */}
                            <Box sx={{ width: 180 }}>
                                <Skeleton
                                    variant="rectangular"
                                    width={80}
                                    height={24}
                                    sx={{ borderRadius: "12px" }}
                                />
                            </Box>

                            {/* Doc Modified Chip */}
                            <Box sx={{ width: 200 }}>
                                <Skeleton
                                    variant="rectangular"
                                    width={90}
                                    height={24}
                                    sx={{ borderRadius: "12px" }}
                                />
                            </Box>
                        </Box>
                    ))}

                    {/* Pagination */}
                    {showPagination && (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 3,
                            py: 2,
                            borderTop: "1px solid rgba(0,0,0,0.12)",
                            backgroundColor: "rgba(0,0,0,0.02)",
                        }}>
                            <Skeleton variant="text" width={160} height={22} />
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Skeleton variant="text" width={120} height={22} />
                                <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: "6px" }} />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Skeleton variant="circular" width={36} height={36} />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DataGridSkeletonForEmailSubscribers;